import { IComboBoxOption } from '@fluentui/react';
import {
  Dropdown,
  getDescriptionForItemCount,
  IconButton,
  iconButtonStylesToggle,
  PageHeader,
  pageHeaderStylesSecondary,
  useClassNames,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { PendingReviewList } from '../../../components/PendingReviewList/PendingReviewList';
import { ReviewStatus } from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { IPendingReviewsClassNames, IPendingReviewsStyles, pendingReviewsStylesDefault } from './PendingReviews.styles';

const getFilterOptions = (title: ContentsTitle) => {
  const filterOptions: IComboBoxOption[] = [
    { key: ReviewStatus.REVIEW_STATUS_TO_REVIEW, text: 'To Review' },
    { key: ReviewStatus.REVIEW_STATUS_APPROVED, text: 'Approved' },
    { key: ReviewStatus.REVIEW_STATUS_REJECTED, text: 'Rejected' },
  ];
  if (title === ContentsTitle.pendingFeatureSets) {
    filterOptions.unshift({ key: ReviewStatus.REVIEW_STATUS_CREATED, text: 'Created' });
  }
  return filterOptions;
};

const getDefaultFilter = (title: ContentsTitle) =>
  title === ContentsTitle.pendingReviews
    ? [ReviewStatus.REVIEW_STATUS_TO_REVIEW]
    : [ReviewStatus.REVIEW_STATUS_CREATED, ReviewStatus.REVIEW_STATUS_TO_REVIEW];

interface IPendingReviewsProps {
  title?: ContentsTitle;
}

export const PendingReviews = ({ title = ContentsTitle.pendingReviews }: IPendingReviewsProps) => {
  const [isCardView, setIsCardView] = useState(true);
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState<ReviewStatus[]>(getDefaultFilter(title));
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: ContentsTitle) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const onChangeFilters = (_e: FormEvent<HTMLDivElement>, option?: IComboBoxOption) => {
    if (option) {
      const state = option.key as ReviewStatus;
      if (option.selected) {
        if (!filters.includes(state)) {
          setFilters([...filters, state]);
        }
      } else {
        if (filters.includes(state)) {
          setFilters(filters.filter((s) => s !== state));
        }
      }
    }
  };
  useEffect(() => {
    setFilters(getDefaultFilter(title));
  }, [title]);
  const classNames = useClassNames<IPendingReviewsStyles, IPendingReviewsClassNames>(
    'PendingReviews',
    pendingReviewsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return (
    <div className={classNames.root} data-test="access-control">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        pageTitle={title}
        description={description}
        secondaryActions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Dropdown
              placeholder="Filter by status"
              data-test="dropdown-pending-reviews-filter"
              multiSelect
              selectedKeys={filters}
              options={getFilterOptions(title)}
              onChange={onChangeFilters}
            />
            <div style={{ paddingTop: '6px' }}>
              <IconButton
                style={{ marginRight: 8 }}
                styles={iconButtonStylesToggle}
                iconName="ExpandMenu"
                checked={isCardView}
                onClick={() => {
                  setIsCardView(true);
                }}
              />
              <IconButton
                styles={iconButtonStylesToggle}
                iconName="CollapseMenu"
                checked={!isCardView}
                onClick={() => {
                  setIsCardView(false);
                }}
              />
            </div>
          </div>
        }
      />
      <PendingReviewList title={title} filters={filters} isCardView={isCardView} onUpdateData={onUpdateData} />
    </div>
  );
};
